import { forwardRef } from 'react';
import PropTypes from 'prop-types';

const getLoaderColorClassName = (color, variant) => {
  if (color === 'default') return 'fill-primary-700';
  if (variant === 'outlined') {
    if (color === 'primary') return 'fill-primary-700';
    if (color === 'destructive') return 'fill-red-400';
  }
  return 'fill-gray-50';
};

const Loader = ({ color, variant }) => {
  return (
    <svg
      aria-hidden="true"
      role="status"
      className="mr-3 inline size-4 animate-spin text-white-100"
      viewBox="0 0 100 101"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
        className={getLoaderColorClassName(color, variant)}
      />
      <path
        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
        fill="currentColor"
      />
    </svg>
  );
};

Loader.propTypes = {
  color: PropTypes.oneOf(['default', 'primary', 'destructive']),
  variant: PropTypes.oneOf(['filled', 'outlined']),
};

Loader.defaultProps = {
  color: 'default',
  variant: 'filled',
};

const commonClassName = `
  focus:ring-offset-1
  focus:ring-2
  focus:outline-none
  focus:ring-primary-300
  active:ring-0
  text-base
  font-medium
  rounded-lg
  py-2.5
  text-center
  inline-flex
  items-center
  justify-center
  shadow-sm
  disabled:opacity-60
  disabled:cursor-not-allowed
`;

const getIconClassName = (size = 'md', position = 'left') => {
  if (position === 'right') {
    switch (size) {
      case 'sm':
        return 'ml-2';
      case 'lg':
      case 'xl':
        return 'ml-3';
      default:
        return 'ml-2.5';
    }
  }

  switch (size) {
    case 'sm':
      return 'mr-2';
    case 'lg':
    case 'xl':
      return 'mr-3';
    default:
      return 'mr-2.5';
  }
};

const getColorClassName = (color = 'default', variant = 'filled') => {
  if (color === 'primary') {
    if (variant === 'outlined') {
      return `
        text-primary-700
        bg-white-100
        border
        border-solid
        border-gray-150
        enabled:hover:text-primary-600
        enabled:focus:text-primary-600
        enabled:active:text-primary-800
      `;
    }

    return `
      text-white-100
      bg-primary-700
      enabled:hover:bg-primary-600
      enabled:focus:bg-primary-600
      enabled:active:bg-primary-800
    `;
  }

  if (color === 'destructive') {
    if (variant === 'outlined') {
      return `
        text-red-500
        bg-white-100
        border
        border-solid
        border-gray-150
        enabled:hover:text-red-400
        enabled:focus:text-red-400
        enabled:active:text-red-600
      `;
    }

    return `
      text-white-100
      bg-red-500
      enabled:hover:bg-red-400
      enabled:focus:bg-red-400
      enabled:active:bg-red-600
    `;
  }

  if (variant === 'outlined') {
    return `
      enabled:hover:text-primary-600
      enabled:focus:text-primary-600
      enabled:active:text-primary-800
    `;
  }

  return `
    text-gray-950
    bg-white-100
    border
    border-solid
    border-gray-150
    enabled:hover:text-primary-600
    enabled:focus:text-primary-600
    enabled:active:text-primary-800
  `;
};

const getSizeClassName = (size = 'md') => {
  switch (size) {
    case 'sm':
      return 'text-base px-2.5 h-8';
    case 'lg':
      return 'text-lg px-4 h-12';
    case 'xl':
      return 'text-lg px-4 h-14';
    default:
      return 'text-base px-3.5 h-10';
  }
};

const getClassName = (color, size, variant, className) => {
  const colorClassName = getColorClassName(color, variant);
  const sizeClassName = getSizeClassName(size);
  return `${commonClassName} ${colorClassName} ${sizeClassName} ${className}`;
};

const Button = forwardRef(
  (
    {
      title,
      type,
      color,
      size,
      variant,
      onClick,
      className,
      loading,
      disabled,
      LeftIcon,
      RightIcon,
      href,
      target,
      rel,
      iconProps,
      titleClassName,
      ...rest
    },
    ref,
  ) => {
    if (href) {
      return (
        <div className={disabled ? 'cursor-not-allowed' : undefined}>
          <a
            href={href}
            target={target}
            rel={rel}
            className={`${disabled ? 'pointer-events-none' : ''} ${getClassName(color, size, variant, className)}`}
            onClick={onClick}
            {...rest}
            ref={ref}
          >
            {LeftIcon && <LeftIcon className={`!text-[20px] ${getIconClassName(size, 'left')}`} {...iconProps} />}
            <span className={titleClassName}>{title}</span>
            {RightIcon && <RightIcon className={`!text-[20px] ${getIconClassName(size, 'right')}`} {...iconProps} />}
          </a>
        </div>
      );
    }

    return (
      <button
        type={type}
        className={getClassName(color, size, variant, className)}
        onClick={onClick}
        disabled={disabled}
        {...rest}
        ref={ref}
      >
        {!loading && LeftIcon && (
          <LeftIcon
            className={`!text-[20px] [&>path]:stroke-current ${getIconClassName(size, 'left')}`}
            {...iconProps}
          />
        )}
        {loading && <Loader color={color} variant={variant} />}
        <span className={titleClassName}>{title}</span>
        {RightIcon && (
          <RightIcon
            className={`!text-[20px] [&>path]:stroke-current ${getIconClassName(size, 'right')}`}
            {...iconProps}
          />
        )}
      </button>
    );
  },
);

Button.displayName = 'Button';

Button.propTypes = {
  title: PropTypes.string.isRequired,
  type: PropTypes.string,
  color: PropTypes.oneOf(['default', 'primary', 'destructive']),
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  variant: PropTypes.oneOf(['filled', 'outlined']),
  onClick: PropTypes.func,
  className: PropTypes.string,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  LeftIcon: PropTypes.object,
  RightIcon: PropTypes.object,
  href: PropTypes.string,
  target: PropTypes.string,
  rel: PropTypes.string,
  iconProps: PropTypes.object,
  titleClassName: PropTypes.string,
};

Button.defaultProps = {
  type: 'button',
  color: 'default',
  size: 'md',
  variant: 'filled',
  className: '',
  loading: false,
  disabled: false,
  iconProps: { size: 18 },
  titleClassName: '',
};

export default Button;
